<div class="wizard-step-content">
  <h3 class="mb-4">
    <i class="fa fa-check-circle text-primary me-2"></i>
    {{ 'eventWizard.review.title' | translate }}
  </h3>
  <p class="text-muted mb-6">{{ 'eventWizard.review.subtitle' | translate }}</p>

  @if (loading) {
    <div class="d-flex justify-content-center py-10">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'common.loading' | translate }}</span>
      </div>
    </div>
  } @else if (!event) {
    <div class="alert alert-warning">
      <i class="fa fa-exclamation-triangle me-2"></i>
      {{ 'eventWizard.review.noEventData' | translate }}
    </div>
  } @else {
    <!-- Event Overview Card -->
    <div class="card mb-5">
      <div class="card-header">
        <h4 class="card-title mb-0">
          <i class="fa fa-info-circle text-primary me-2"></i>
          {{ 'eventWizard.review.basicInfo' | translate }}
        </h4>
        <a class="btn btn-sm btn-light" (click)="navigateToStep('basic')">
          <i class="fa fa-edit me-1"></i>{{ 'common.edit' | translate }}
        </a>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-md-6 mb-4">
            <label class="text-muted fs-7">{{ 'eventWizard.review.eventName' | translate }}</label>
            <div class="fw-bold">{{ event.event_name }}</div>
          </div>
          <div class="col-md-6 mb-4">
            <label class="text-muted fs-7">{{ 'eventWizard.review.eventType' | translate }}</label>
            <div class="fw-bold">{{ event.event_type?.event_type_name || '-' }}</div>
          </div>
          <div class="col-md-6 mb-4">
            <label class="text-muted fs-7">{{ 'eventWizard.review.startDate' | translate }}</label>
            <div class="fw-bold">{{ formatDate(event.start_date!) }}</div>
          </div>
          <div class="col-md-6 mb-4">
            <label class="text-muted fs-7">{{ 'eventWizard.review.endDate' | translate }}</label>
            <div class="fw-bold">{{ formatDate(event.end_date!) }}</div>
          </div>
          <div class="col-md-6 mb-4">
            <label class="text-muted fs-7">{{ 'eventWizard.review.registrationDeadline' | translate }}</label>
            <div class="fw-bold">{{ event.registration_deadline ? formatDate(event.registration_deadline) : ('eventWizard.review.notSet' | translate) }}</div>
          </div>
          <div class="col-md-6 mb-4">
            <label class="text-muted fs-7">{{ 'eventWizard.review.location' | translate }}</label>
            <div class="fw-bold">
              @if (event.city) {
                {{ event.address ? event.address + ', ' : '' }}{{ event.city }}
              } @else {
                {{ 'eventWizard.review.notSet' | translate }}
              }
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Summary Cards -->
    <div class="row g-4 mb-5">
      <!-- Competition: Classes -->
      @if (category === 'competition') {
        <div class="col-md-6 col-lg-3">
          <div class="card h-100" [class.border-success]="classCount > 0" [class.border-danger]="classCount === 0">
            <div class="card-body text-center">
              <i class="fa fa-list fs-2x mb-3" [class.text-success]="classCount > 0" [class.text-danger]="classCount === 0"></i>
              <h2 class="mb-1">{{ classCount }}</h2>
              <div class="text-muted">{{ 'eventWizard.review.classes' | translate }}</div>
              @if (classCount === 0) {
                <span class="badge badge-light-danger mt-2">{{ 'eventWizard.review.required' | translate }}</span>
              }
              <div class="mt-3">
                <a class="btn btn-sm btn-light" (click)="navigateToStep('classes')">
                  <i class="fa fa-edit me-1"></i>{{ 'common.edit' | translate }}
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Competition: Schedules -->
        <div class="col-md-6 col-lg-3">
          <div class="card h-100">
            <div class="card-body text-center">
              <i class="fa fa-calendar-alt fs-2x mb-3 text-primary"></i>
              <h2 class="mb-1">{{ scheduleCount }}</h2>
              <div class="text-muted">{{ 'eventWizard.review.schedules' | translate }}</div>
              <span class="badge badge-light-info mt-2">{{ 'eventWizard.review.optional' | translate }}</span>
              <div class="mt-3">
                <a class="btn btn-sm btn-light" (click)="navigateToStep('schedule')">
                  <i class="fa fa-edit me-1"></i>{{ 'common.edit' | translate }}
                </a>
              </div>
            </div>
          </div>
        </div>
      }

      <!-- Training: Capacity -->
      @if (category === 'training') {
        <div class="col-md-6 col-lg-3">
          <div class="card h-100" [class.border-success]="capacityCount > 0" [class.border-danger]="capacityCount === 0">
            <div class="card-body text-center">
              <i class="fa fa-calendar-check fs-2x mb-3" [class.text-success]="capacityCount > 0" [class.text-danger]="capacityCount === 0"></i>
              <h2 class="mb-1">{{ capacityCount }}</h2>
              <div class="text-muted">{{ 'eventWizard.review.daysConfigured' | translate }}</div>
              @if (capacityCount === 0) {
                <span class="badge badge-light-danger mt-2">{{ 'eventWizard.review.required' | translate }}</span>
              }
              <div class="mt-3">
                <a class="btn btn-sm btn-light" (click)="navigateToStep('capacity')">
                  <i class="fa fa-edit me-1"></i>{{ 'common.edit' | translate }}
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Training: Packages -->
        <div class="col-md-6 col-lg-3">
          <div class="card h-100" [class.border-success]="packageCount > 0" [class.border-danger]="packageCount === 0">
            <div class="card-body text-center">
              <i class="fa fa-box-open fs-2x mb-3" [class.text-success]="packageCount > 0" [class.text-danger]="packageCount === 0"></i>
              <h2 class="mb-1">{{ packageCount }}</h2>
              <div class="text-muted">{{ 'eventWizard.review.packages' | translate }}</div>
              @if (packageCount === 0) {
                <span class="badge badge-light-danger mt-2">{{ 'eventWizard.review.required' | translate }}</span>
              }
              <div class="mt-3">
                <a class="btn btn-sm btn-light" (click)="navigateToStep('packages')">
                  <i class="fa fa-edit me-1"></i>{{ 'common.edit' | translate }}
                </a>
              </div>
            </div>
          </div>
        </div>
      }

      <!-- Services (all event types) -->
      <div class="col-md-6 col-lg-3">
        <div class="card h-100">
          <div class="card-body text-center">
            <i class="fa fa-concierge-bell fs-2x mb-3 text-info"></i>
            <h2 class="mb-1">{{ serviceCount }}</h2>
            <div class="text-muted">{{ 'eventWizard.review.services' | translate }}</div>
            <span class="badge badge-light-info mt-2">{{ 'eventWizard.review.optional' | translate }}</span>
            <div class="mt-3">
              <a class="btn btn-sm btn-light" (click)="navigateToStep('services')">
                <i class="fa fa-edit me-1"></i>{{ 'common.edit' | translate }}
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Contacts (all event types) -->
      <div class="col-md-6 col-lg-3">
        <div class="card h-100">
          <div class="card-body text-center">
            <i class="fa fa-address-book fs-2x mb-3 text-primary"></i>
            <h2 class="mb-1">{{ contactCount }}</h2>
            <div class="text-muted">{{ 'eventWizard.review.contacts' | translate }}</div>
            <span class="badge badge-light-info mt-2">{{ 'eventWizard.review.optional' | translate }}</span>
            <div class="mt-3">
              <a class="btn btn-sm btn-light" (click)="navigateToStep('contacts')">
                <i class="fa fa-edit me-1"></i>{{ 'common.edit' | translate }}
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Sponsors (all event types) -->
      <div class="col-md-6 col-lg-3">
        <div class="card h-100">
          <div class="card-body text-center">
            <i class="fa fa-handshake fs-2x mb-3 text-warning"></i>
            <h2 class="mb-1">{{ sponsorCount }}</h2>
            <div class="text-muted">{{ 'eventWizard.review.sponsors' | translate }}</div>
            <span class="badge badge-light-info mt-2">{{ 'eventWizard.review.optional' | translate }}</span>
            <div class="mt-3">
              <a class="btn btn-sm btn-light" (click)="navigateToStep('sponsors')">
                <i class="fa fa-edit me-1"></i>{{ 'common.edit' | translate }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Payment Settings Card -->
    <div class="card mb-5" [class.border-danger]="hasPaymentIssue">
      <div class="card-header">
        <h4 class="card-title mb-0">
          <i class="fa fa-credit-card me-2" [class.text-primary]="!hasPaymentIssue" [class.text-danger]="hasPaymentIssue"></i>
          {{ 'eventWizard.review.paymentSettings' | translate }}
          @if (hasPaymentIssue) {
            <span class="badge badge-light-danger ms-2">{{ 'eventWizard.review.actionRequired' | translate }}</span>
          }
        </h4>
        <a class="btn btn-sm btn-light" (click)="navigateToStep('payment')">
          <i class="fa fa-edit me-1"></i>{{ 'common.edit' | translate }}
        </a>
      </div>
      <div class="card-body">
        @if (hasPaymentIssue) {
          <div class="d-flex align-items-center">
            <i class="fa fa-exclamation-triangle text-danger fs-2 me-3"></i>
            <div>
              <strong class="text-danger">{{ 'eventWizard.review.paymentIssue' | translate }}</strong>
              <div class="text-muted">{{ 'eventWizard.review.paymentIssueDesc' | translate }}</div>
            </div>
          </div>
        } @else if (hasPaymentSetup && isPaymentEnabled) {
          <div class="d-flex align-items-center">
            <i class="fa fa-check-circle text-success fs-2 me-3"></i>
            <div>
              <strong>{{ 'eventWizard.review.paymentsEnabled' | translate }}</strong>
              <div class="text-muted">{{ paymentTiming }} {{ 'eventWizard.review.paymentRequired' | translate }}</div>
            </div>
          </div>
        } @else if (hasPaymentSetup && !isPaymentEnabled) {
          <div class="d-flex align-items-center">
            <i class="fa fa-times-circle text-muted fs-2 me-3"></i>
            <div>
              <strong>{{ 'eventWizard.review.paymentsDisabled' | translate }}</strong>
              <div class="text-muted">{{ 'eventWizard.review.registrationsFree' | translate }}</div>
            </div>
          </div>
        } @else {
          <div class="d-flex align-items-center">
            <i class="fa fa-info-circle text-info fs-2 me-3"></i>
            <div>
              <strong>{{ 'eventWizard.review.noPaymentSetup' | translate }}</strong>
              <div class="text-muted">{{ 'eventWizard.review.noPaymentSetupDesc' | translate }}</div>
            </div>
          </div>
        }
      </div>
    </div>

    <!-- Publish Status -->
    @if (isReadyToPublish) {
      <div class="alert alert-light-success border border-success border-dashed">
        <div class="d-flex align-items-center">
          <i class="fa fa-rocket text-success fs-2 me-3"></i>
          <div>
            <strong>{{ 'eventWizard.review.readyToPublish' | translate }}</strong>
            <div class="text-muted">{{ 'eventWizard.review.readyToPublishDesc' | translate }}</div>
          </div>
        </div>
      </div>
    } @else {
      <div class="alert border border-dashed" [class.alert-light-danger]="hasPaymentIssue" [class.border-danger]="hasPaymentIssue" [class.alert-light-warning]="!hasPaymentIssue" [class.border-warning]="!hasPaymentIssue">
        <div class="d-flex align-items-center">
          <i class="fa fa-exclamation-triangle fs-2 me-3" [class.text-danger]="hasPaymentIssue" [class.text-warning]="!hasPaymentIssue"></i>
          <div>
            <strong [class.text-danger]="hasPaymentIssue">{{ 'eventWizard.review.notReadyToPublish' | translate }}</strong>
            <div class="text-muted">{{ publishRequirementMessage }}</div>
          </div>
        </div>
      </div>
    }
  }
</div>
